import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { PageLayout } from '../components/Layout';
import TrialOfferPopup from '../components/TrialOfferPopup';
import axios from 'axios';
import { Calendar, ChevronRight, TrendingUp, AlertCircle, Crown, Clock, Sparkles, AlertTriangle, CheckCircle2, BarChart3, X, Plus, Lightbulb, HelpCircle } from 'lucide-react';
import { getCurrencySymbol } from '../utils/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTrialOffer, setShowTrialOffer] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dashRes, subRes] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard`),
        axios.get(`${API_URL}/api/subscription/status`)
      ]);
      setDashboard(dashRes.data);
      setSubscription(subRes.data);
      // Show trial offer on first dashboard visit if eligible
      if (subRes.data.show_trial_offer) {
        const dismissed = sessionStorage.getItem('trial_offer_dismissed');
        if (!dismissed) {
          setShowTrialOffer(true);
        }
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const getDaysLabel = (days) => {
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    return `${days} days`;
  };

  const isPremium = subscription?.tier === 'premium';
  const atLimit = subscription && !subscription.can_add_more;
  const isNewUser = !loading && (dashboard?.payment_count ?? 0) === 0;

  // Activation nudge: free user with 1-2 active debits, not dismissed
  const activeCount = dashboard?.payment_count ?? 0;
  const nudgeDismissedKey = user?.id ? `mdd_activation_nudge_dismissed_${user.id}` : null;
  const [activationNudgeDismissed, setActivationNudgeDismissed] = useState(false);
  useEffect(() => {
    if (nudgeDismissedKey) {
      setActivationNudgeDismissed(localStorage.getItem(nudgeDismissedKey) === '1');
    }
  }, [nudgeDismissedKey]);
  const showActivationNudge =
    !isPremium &&
    activeCount >= 1 &&
    activeCount < 3 &&
    !activationNudgeDismissed;
  const nudgeRemaining = 3 - activeCount;
  const dismissActivationNudge = () => {
    if (nudgeDismissedKey) {
      try { localStorage.setItem(nudgeDismissedKey, '1'); } catch { /* ignore */ }
    }
    setActivationNudgeDismissed(true);
  };

  // Renewal & upgrade logic
  const isMonthly = isPremium && subscription?.premium_plan === 'monthly';
  const renewalDaysLeft = (() => {
    if (!isPremium || !subscription?.premium_since) return null;
    const since = new Date(subscription.premium_since);
    const now = new Date();
    const renewalDate = new Date(since);
    if (subscription.premium_plan === 'monthly') {
      // Find next monthly renewal
      renewalDate.setMonth(renewalDate.getMonth() + 1);
      while (renewalDate < now) renewalDate.setMonth(renewalDate.getMonth() + 1);
    } else {
      // Find next yearly renewal
      renewalDate.setFullYear(renewalDate.getFullYear() + 1);
      while (renewalDate < now) renewalDate.setFullYear(renewalDate.getFullYear() + 1);
    }
    const diff = Math.ceil((renewalDate - now) / (1000 * 60 * 60 * 24));
    return diff;
  })();
  const showRenewalBanner = isPremium && renewalDaysLeft !== null && renewalDaysLeft <= 14;

  if (loading) {
    return (
      <PageLayout showNav showUpgrade isPremium={isPremium}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout showNav showUpgrade isPremium={isPremium}>
      {showTrialOffer && (
        <TrialOfferPopup
          onClose={() => {
            setShowTrialOffer(false);
            sessionStorage.setItem('trial_offer_dismissed', 'true');
          }}
        />
      )}
      <div className="px-4 py-6 max-w-lg mx-auto space-y-6">
        {/* Greeting */}
        <div className="animate-fade-in">
          <h1 className="font-heading font-bold text-dark text-2xl">
            Hello, {user?.name?.split(' ')[0] || 'there'}
          </h1>
          <p className="text-text-secondary text-sm">{isNewUser ? "Let's get you set up" : 'Stay in control'}</p>
        </div>

        {/* New-user onboarding hero — shows ONLY when zero debits */}
        {isNewUser && <FirstDebitOnboarding />}

        {/* One-time Spending Insight Report onboarding banner */}
        {dashboard?.show_spending_report_banner && (
          <SpendingReportBanner
            onDismiss={async () => {
              try {
                await axios.post(`${API_URL}/api/auth/dismiss-spending-report-banner`);
                setDashboard((d) => d ? { ...d, show_spending_report_banner: false } : d);
              } catch (err) {
                // fail silently; the banner will return next load which is acceptable
                setDashboard((d) => d ? { ...d, show_spending_report_banner: false } : d);
              }
            }}
          />
        )}

        {/* This Month Card — hide when no debits (new user sees onboarding hero instead) */}
        {!isNewUser && (
          <div 
            className="bg-primary rounded-2xl p-6 text-white shadow-lg animate-slide-up"
            data-testid="this-month-card"
          >
          <div className="flex items-center justify-between mb-4">
            <span className="text-white/80 text-sm font-medium">This Month</span>
            <Calendar className="w-5 h-5 text-white/60" />
          </div>
          <div className="text-4xl font-heading font-bold mb-1">
            {getCurrencySymbol(user?.default_currency)}{dashboard?.total_due?.toFixed(2) || '0.00'}
          </div>
          <p className="text-white/70 text-sm">
            {dashboard?.payment_count || 0} payment{dashboard?.payment_count !== 1 ? 's' : ''} tracked
          </p>
        </div>
        )}

        {/* Estimated Available Balance */}
        {dashboard?.has_estimated_balance && (
          <div 
            className={`rounded-2xl p-6 shadow-lg animate-slide-up ${
              dashboard.estimated_balance < 0 
                ? 'bg-red-50 border-2 border-red-200' 
                : 'bg-amber-50 border-2 border-amber-200'
            }`}
            data-testid="estimated-balance-card"
          >
            <div className="flex items-center justify-between mb-4">
              <span className={`text-sm font-medium ${dashboard.estimated_balance < 0 ? 'text-red-600' : 'text-amber-700'}`}>
                Estimated Available Balance
              </span>
              {dashboard.estimated_balance < 0 && <AlertTriangle className="w-5 h-5 text-red-500" />}
            </div>
            <div className={`text-4xl font-heading font-bold mb-1 ${
              dashboard.estimated_balance < 0 ? 'text-red-600' : 'text-amber-800'
            }`}>
              {dashboard.estimated_balance < 0 ? '-' : ''}{getCurrencySymbol(user?.default_currency)}{Math.abs(dashboard.estimated_balance).toFixed(2)}
            </div>
            <p className={`text-sm ${dashboard.estimated_balance < 0 ? 'text-red-500' : 'text-amber-600'}`}>
              {dashboard.estimated_balance < 0 
                ? 'You may go into your overdraft or incur fees which could apply.' 
                : 'After upcoming payments this month'}
            </p>
            <p className={`text-xs mt-2 ${dashboard.estimated_balance < 0 ? 'text-red-400' : 'text-amber-500'}`}>
              Estimate
            </p>
          </div>
        )}

        {/* Recently Cancelled — feel-good "money saved" badge */}
        {dashboard?.recently_cancelled?.count > 0 && (
          <RecentlyCancelledBadge
            count={dashboard.recently_cancelled.count}
            savings={dashboard.recently_cancelled.monthly_savings}
            currency={dashboard.recently_cancelled.currency}
          />
        )}

        {/* Largest Upcoming */}
        {dashboard?.largest_upcoming && (
          <div 
            className="card-default p-5 animate-fade-in"
            data-testid="largest-upcoming-card"
          >
            <div className="flex items-center gap-2 text-text-secondary text-sm mb-3">
              <TrendingUp className="w-4 h-4" />
              <span>Largest Upcoming</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="font-semibold text-dark">{dashboard.largest_upcoming.company_name}</span>
              <span className="font-heading font-bold text-xl text-dark">
                {getCurrencySymbol(dashboard.largest_upcoming.currency)}{dashboard.largest_upcoming.amount.toFixed(2)}
              </span>
            </div>
          </div>
        )}

        {/* Activation Nudge — free user with 1-2 active debits */}
        {showActivationNudge && (
          <div
            className="card-default p-5 border-l-4 border-primary animate-fade-in relative"
            data-testid="activation-nudge"
          >
            <button
              onClick={dismissActivationNudge}
              data-testid="activation-nudge-dismiss"
              className="absolute top-3 right-3 p-1 text-text-light hover:text-dark transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-start gap-3 pr-6">
              <div className="w-10 h-10 bg-primary-light rounded-full flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-dark mb-1">
                  Add {nudgeRemaining} more debit{nudgeRemaining !== 1 ? 's' : ''} to unlock your full Spending Insight Report
                </p>
                <p className="text-sm text-text-secondary mb-3">
                  See your real monthly outgoings, your largest payments, and exactly what's leaving your account next.
                </p>
                <Link
                  to="/add"
                  data-testid="activation-nudge-cta"
                  className="btn-primary inline-flex items-center gap-1.5 px-4 py-2 text-sm"
                >
                  <Plus className="w-4 h-4" /> Add a debit
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Free Limit Banner */}
        {atLimit && !isPremium && (
          <Link 
            to="/upgrade"
            className="block card-premium p-5 animate-fade-in"
            data-testid="free-limit-banner"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gold rounded-full flex items-center justify-center">
                <Crown className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-dark">Free limit reached</p>
                <p className="text-sm text-text-secondary">Upgrade for unlimited direct debits</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gold" />
            </div>
          </Link>
        )}

        {/* Renewal Reminder Banner */}
        {showRenewalBanner && (
          <div 
            className="card-default p-5 border-l-4 border-gold animate-fade-in"
            data-testid="renewal-reminder-banner"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gold-light rounded-full flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-gold" />
              </div>
              <div>
                <p className="font-semibold text-dark">
                  Renewal in {renewalDaysLeft} day{renewalDaysLeft !== 1 ? 's' : ''}
                </p>
                <p className="text-sm text-text-secondary">
                  Your {subscription?.premium_plan} premium plan renews soon. Manage in Settings.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Monthly → Annual Upgrade Nudge */}
        {isMonthly && (
          <Link 
            to="/upgrade"
            className="block card-default p-5 border-l-4 border-primary animate-fade-in"
            data-testid="monthly-upgrade-nudge"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary-light rounded-full flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-dark">Save with the annual plan</p>
                <p className="text-sm text-text-secondary">
                  You're paying £60/year on monthly. Switch to annual for £35/year &mdash; use code <span className="font-bold text-primary">EARLYMDD</span> at checkout!
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-primary flex-shrink-0" />
            </div>
          </Link>
        )}

        {/* Next 7 Days — hide when new user (onboarding hero already covers this) */}
        {!isNewUser && (
        <div className="animate-fade-in">
          <h2 className="font-heading font-semibold text-dark text-lg mb-4">Next 7 Days</h2>
          
          {dashboard?.next_7_days?.length > 0 ? (
            <div className="space-y-3">
              {dashboard.next_7_days.map((debit) => (
                <Link
                  key={debit.id}
                  to={`/edit/${debit.id}`}
                  className="card-default p-4 flex items-center gap-4 hover:-translate-y-0.5 hover:shadow-md transition-all"
                  data-testid={`upcoming-debit-${debit.id}`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    debit.days_until <= 1 ? 'bg-alert-light' : 'bg-primary-light'
                  }`}>
                    {debit.days_until <= 1 ? (
                      <AlertCircle className="w-5 h-5 text-alert" />
                    ) : (
                      <Calendar className="w-5 h-5 text-primary" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-dark truncate">{debit.company_name}</p>
                    <p className="text-sm text-text-light">{debit.next_payment_date}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-heading font-bold text-dark">{getCurrencySymbol(debit.currency)}{debit.amount.toFixed(2)}</p>
                    <p className={`text-xs font-medium ${
                      debit.days_until <= 1 ? 'text-alert' : 'text-text-light'
                    }`}>
                      {getDaysLabel(debit.days_until)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="card-default p-8 text-center">
              <Calendar className="w-12 h-12 text-text-light mx-auto mb-3" />
              <p className="text-text-secondary">No upcoming payments in the next 7 days</p>
            </div>
          )}
        </div>
        )}

        {/* Upcoming Payments */}
        {dashboard?.all_upcoming?.length > 0 && (
          <div className="animate-fade-in">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-dark text-lg">Upcoming Payments</h2>
              <Link to="/debits" className="text-primary text-sm font-medium hover:underline">
                See all
              </Link>
            </div>
            
            <div className="space-y-2">
              {dashboard.all_upcoming.slice(0, 5).map((debit) => (
                <Link
                  key={debit.id}
                  to={`/edit/${debit.id}`}
                  className="card-default p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  data-testid={`payment-${debit.id}`}
                >
                  <div>
                    <p className="font-medium text-dark">{debit.company_name}</p>
                    <p className="text-xs text-text-light capitalize">{debit.frequency}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-dark">{getCurrencySymbol(debit.currency)}{debit.amount.toFixed(2)}</p>
                    <p className="text-xs text-text-light">{debit.next_payment_date}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Empty State — only when user has had debits but cleared/cancelled them all (NOT for brand-new users — they see the onboarding hero above) */}
        {!isNewUser && !dashboard?.all_upcoming?.length && (
          <div className="card-default p-8 text-center animate-fade-in">
            <div className="w-16 h-16 bg-primary-light rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-8 h-8 text-primary" />
            </div>
            <h3 className="font-heading font-semibold text-dark text-lg mb-2">
              No direct debits yet
            </h3>
            <p className="text-text-secondary mb-6">
              Start tracking your payments to stay in control
            </p>
            <Link
              to="/add"
              data-testid="add-first-debit-btn"
              className="btn-primary inline-block px-6 py-3"
            >
              Add Your First Direct Debit
            </Link>
          </div>
        )}
      </div>
    </PageLayout>
  );
}

function RecentlyCancelledBadge({ count, savings, currency }) {
  const sym = getCurrencySymbol(currency);
  const niceAmount = `${sym}${(savings || 0).toFixed(2)}`;
  const debitWord = count === 1 ? 'debit' : 'debits';

  return (
    <div className="card-default p-4 sm:p-5 animate-fade-in" data-testid="recently-cancelled-badge">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-primary-light flex items-center justify-center flex-shrink-0">
          <CheckCircle2 className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-dark text-sm sm:text-base leading-tight">
            {count} {debitWord} marked as cancelled this month
          </p>
          <p className="text-text-secondary text-sm mt-1">
            {niceAmount}/mo no longer in your active list.
          </p>
          <p className="text-xs text-text-light mt-2 leading-relaxed">
            Record of your actions in MDD. To fully stop a debit: contact your bank <strong>and</strong> delete the reminder from your calendar.
          </p>
        </div>
      </div>
    </div>
  );
}


function SpendingReportBanner({ onDismiss }) {
  const handleStart = () => {
    // Mark dismissed, then open the quiz in a NEW TAB so the MDD login
    // session stays intact in the original tab. When the user is done,
    // they just switch back to the MDD tab — still signed in.
    onDismiss();
    window.open(
      'https://www.qualiquiz.app/quiz/find-out-where-your-money-is-going-each-month',
      '_blank',
      'noopener,noreferrer'
    );
  };

  return (
    <div
      className="relative rounded-2xl p-5 shadow-sm animate-fade-in border-2"
      style={{ background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)', borderColor: '#F59E0B' }}
      data-testid="spending-report-banner"
    >
      <button
        type="button"
        onClick={onDismiss}
        data-testid="spending-report-banner-close"
        aria-label="Dismiss"
        className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/60 hover:bg-white text-text-secondary hover:text-dark flex items-center justify-center transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <div className="w-11 h-11 rounded-full bg-dark flex items-center justify-center flex-shrink-0">
          <BarChart3 className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-heading font-bold text-dark text-base sm:text-lg leading-tight">
            Your Free Spending Insight Report is ready
          </p>
          <p className="text-text-secondary text-sm mt-1 leading-relaxed">
            Complete the assessment to receive personalised results and understand what's really leaving your account.
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={handleStart}
        data-testid="spending-report-start-btn"
        className="mt-4 w-full py-3 rounded-xl font-heading font-bold text-white text-base bg-dark hover:bg-primary-hover transition-colors flex items-center justify-center gap-2 shadow-sm"
      >
        Start My Assessment
      </button>
      <p className="text-xs text-text-light text-center mt-3">
        You can also find the assessment link in your welcome email.
      </p>
    </div>
  );
}

function FirstDebitOnboarding() {
  const examples = [
    { name: 'Netflix', amount: '£15.99', emoji: '🎬' },
    { name: 'Council tax', amount: '£165', emoji: '🏛️' },
    { name: 'Gym membership', amount: '£35', emoji: '💪' },
  ];
  return (
    <div
      className="bg-primary rounded-2xl p-6 text-white shadow-lg animate-slide-up"
      data-testid="first-debit-onboarding"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 bg-white/15 rounded-full flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <h2 className="font-heading font-bold text-white text-xl leading-tight">
            Add your first direct debit
          </h2>
          <p className="text-white/85 text-sm mt-1">
            Takes 30 seconds. No bank login needed — you just type the name, amount and payment day.
          </p>
        </div>
      </div>

      <Link
        to="/add"
        data-testid="onboarding-add-first-btn"
        className="block w-full bg-white text-primary text-center font-heading font-bold py-3 rounded-xl shadow-sm hover:bg-white/95 transition-colors mb-4"
      >
        + Add my first direct debit
      </Link>

      <div className="bg-white/10 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Lightbulb className="w-4 h-4 text-white/80" />
          <p className="text-xs text-white/85 font-semibold uppercase tracking-wide">
            Common ones to track
          </p>
        </div>
        <div className="space-y-2">
          {examples.map((ex) => (
            <div key={ex.name} className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2">
              <span className="text-sm text-white">
                <span className="mr-2">{ex.emoji}</span>{ex.name}
              </span>
              <span className="text-sm font-semibold text-white/90">{ex.amount}</span>
            </div>
          ))}
        </div>
      </div>

      <Link
        to="/how-it-works"
        data-testid="onboarding-how-it-works-link"
        className="mt-4 flex items-center justify-center gap-1.5 text-white/80 hover:text-white text-sm transition-colors"
      >
        <HelpCircle className="w-4 h-4" />
        <span>What is a direct debit?</span>
      </Link>
    </div>
  );
}

